import { useState } from "react";

import { useT } from "../../i18n";
import { useLocaleStore } from "../../store/localeStore";
import { cx } from "../../lib/cx";
import {
  exportSelection,
  type ExportArea,
  type ExportFields,
  type ExportFormat,
} from "../../services/export";
import { useDataStore } from "../../store/dataStore";
import { useFiltersStore } from "../../store/filtersStore";
import { useLayersStore } from "../../store/layersStore";
import { Modal } from "../ui/Modal";

interface Option<T> {
  value: T;
  label: string;
}

function RadioGroup<T extends string>({
  legend,
  name,
  options,
  value,
  onChange,
}: {
  legend: string;
  name: string;
  options: Option<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="mb-3">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        {legend}
      </legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <label
            key={o.value}
            className={cx(
              "cursor-pointer rounded-lg border px-2.5 py-1 text-sm transition-colors",
              value === o.value
                ? "border-accent bg-accent-subtle text-accent"
                : "border-border-default text-text-secondary hover:border-border-strong",
            )}
          >
            <input
              type="radio"
              name={name}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className="sr-only"
            />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function ExportModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const locale = useLocaleStore((s) => s.locale);
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [area, setArea] = useState<ExportArea>("country");
  const [fields, setFields] = useState<ExportFields>("all");
  const [status, setStatus] = useState<"idle" | "busy" | "done" | "empty" | "error">("idle");
  const [count, setCount] = useState(0);

  const submit = async () => {
    setStatus("busy");
    const applied = useFiltersStore.getState().applied;
    const range = useFiltersStore.getState().range;
    const grades = useLayersStore.getState().grades;
    const map = useDataStore.getState().map;
    const bounds = map ? (map.getBounds().toArray() as [[number, number], [number, number]]) : null;

    try {
      const n = await exportSelection({
        format,
        area,
        fields,
        applied,
        grades,
        datasetRange: range,
        locale,
        bounds,
      });
      setCount(n);
      setStatus(n === 0 ? "empty" : "done");
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  };

  return (
    <Modal title={t.export.title} onClose={onClose}>
      <RadioGroup
        legend={t.export.format}
        name="format"
        value={format}
        onChange={setFormat}
        options={[
          { value: "csv", label: "CSV" },
          { value: "geojson", label: "GeoJSON" },
          { value: "xlsx", label: "XLSX" },
        ]}
      />
      <RadioGroup
        legend={t.export.area}
        name="area"
        value={area}
        onChange={setArea}
        options={[
          { value: "country", label: t.export.areaCountry },
          { value: "extent", label: t.export.areaExtent },
        ]}
      />
      <RadioGroup
        legend={t.export.fields}
        name="fields"
        value={fields}
        onChange={setFields}
        options={[
          { value: "all", label: t.export.fieldsAll },
          { value: "basic", label: t.export.fieldsBasic },
        ]}
      />

      <div className="mt-4 flex items-center justify-between gap-3">
        <span className="text-xs text-text-tertiary" role="status">
          {status === "empty" && t.export.empty}
          {status === "done" && `${t.export.done} ${count}`}
          {status === "error" && t.export.error}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-default px-3 py-1.5 text-sm text-text-secondary transition-colors hover:text-text-primary"
          >
            {t.export.cancel}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={status === "busy"}
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-text-onAccent transition-colors hover:bg-accent-hover disabled:opacity-60"
          >
            {status === "busy" ? t.export.exporting : t.export.submit}
          </button>
        </div>
      </div>
    </Modal>
  );
}
